// markup/legend.mjs — the LEGEND stamp: its rows and its layout (pure, no imports).
// ---------------------------------------------------------------------------
// Brett 2026-09-15: "make me a legend button so i can stamp the drawing with
// those colours." A legend is a markup object like any other: one point (its
// top-left corner), a style, and the ROWS it lists — each a catalogue type's
// key, name and colour, copied in when it is stamped. Copied rather than looked
// up, so the legend reads the same wherever the markup is opened, including the
// plan with markup closed, where the catalogue has not been fetched.
//
// Unlike a text note, a legend is a STAMP: its fontSize is in the drawing's
// natural pixels, not screen pixels, so it zooms with the plan and keeps its
// size against the sheet — the way a legend printed on a drawing does.
//
// Every measurement below is a multiple of fontSize. That is what makes the
// box a straight function of one number, so the hit-test, the selection box and
// the corner drag all agree with what is drawn.
// ---------------------------------------------------------------------------

export const LEGEND_TITLE = 'LEGEND';
export const LEGEND_MAX_ROWS = 24;
export const LEGEND_MIN_FONT = 4;
export const LEGEND_MAX_FONT = 2000;
const TOTALS_CHARS = 12; // "12345.67 m²" — reserved, so the box never depends on the numbers
const CHAR_W = 0.6;      // average glyph width as a share of the font size

const KEY_RE = /^[a-z][a-z0-9-]{0,38}[a-z0-9]$/;
const COLOR_RE = /^#(?:[0-9a-f]{3}|[0-9a-f]{6}|[0-9a-f]{8})$/i;

// A catalogue type key, or null. The one rule for what a key may look like on a
// mark or a legend row (a key arrives from imported documents as well as ours).
export function cleanTypeKey(k) {
  if (k == null) return null;
  const s = String(k).trim().toLowerCase();
  return KEY_RE.test(s) ? s : null;
}

export function cleanColor(c) {
  if (typeof c !== 'string') return null;
  const s = c.trim();
  return COLOR_RE.test(s) ? s : null;
}

// Rows with a valid key, a real colour and a name; first of each key wins.
export function sanitizeLegendRows(rows) {
  const out = [];
  const seen = new Set();
  for (const r of Array.isArray(rows) ? rows : []) {
    if (!r || typeof r !== 'object') continue;
    const key = cleanTypeKey(r.key);
    const color = cleanColor(r.color);
    const label = typeof r.label === 'string' ? r.label.trim().slice(0, 60) : '';
    if (!key || !color || !label || seen.has(key)) continue;
    seen.add(key);
    out.push({ key, label, color });
    if (out.length >= LEGEND_MAX_ROWS) break;
  }
  return out;
}

// What a new legend lists: the tenant's active types, in catalogue order.
export function legendRowsFrom(markTypes) {
  return sanitizeLegendRows((markTypes || []).filter((t) => t && t.active !== false));
}

export function clampLegendFont(fs) {
  const n = Number(fs);
  if (!Number.isFinite(n)) return null;
  return Math.max(LEGEND_MIN_FONT, Math.min(LEGEND_MAX_FONT, n));
}

// Layout in natural px, relative to the legend's top-left corner.
export function legendLayout(o) {
  const fs = clampLegendFont(o && o.style && o.style.fontSize) || 14;
  const rows = (o && Array.isArray(o.rows)) ? o.rows : [];
  const totals = !(o && o.totals === false);
  const pad = fs * 0.7;
  const titleH = fs * 1.6;
  const rowH = fs * 1.6;
  const swatch = fs * 1.05;
  const gap = fs * 0.6;
  const labelChars = Math.max(LEGEND_TITLE.length, ...rows.map((r) => String(r.label || '').length));
  const labelX = pad + swatch + gap;
  const totalsW = totals ? gap * 2 + TOTALS_CHARS * fs * CHAR_W : 0;
  const w = labelX + labelChars * fs * CHAR_W + totalsW + pad;
  const h = pad + titleH + rows.length * rowH + pad * 0.6;
  return {
    fs, pad, titleH, rowH, swatch, gap, labelX, w, h, totals,
    totalsX: w - pad, // right edge of the totals column (text-anchor: end)
    rows: rows.map((r, i) => ({ ...r, top: pad + titleH + i * rowH })),
  };
}

// Normalised box {minX,minY,maxX,maxY} — the hit-test, selection and marquee box.
export function legendBoxNorm(o, W, H) {
  const a = (o && o.points && o.points[0]) || { x: 0, y: 0 };
  const L = legendLayout(o);
  return { minX: a.x, minY: a.y, maxX: a.x + L.w / (W || 1), maxY: a.y + L.h / (H || 1) };
}

// Font size that puts the legend's bottom-right corner at `pointerNorm`. The box
// is linear in the font size, so this is a division; the larger of the two
// directions wins, so dragging right OR down grows it.
export function legendFontSizeForDrag(o, pointerNorm, W, H) {
  const a = (o && o.points && o.points[0]) || { x: 0, y: 0 };
  const L100 = legendLayout({ ...o, style: { ...((o && o.style) || {}), fontSize: 100 } });
  const byW = ((pointerNorm.x - a.x) * (W || 1)) / (L100.w / 100);
  const byH = ((pointerNorm.y - a.y) * (H || 1)) / (L100.h / 100);
  return clampLegendFont(Math.max(byW, byH));
}

// Text colour for a chip filled with `color`: whichever of white or near-black
// has the higher WCAG contrast. White on red and blue, dark on yellow.
export function readableInk(color) {
  const c = cleanColor(color);
  if (!c) return '#ffffff';
  let h = c.slice(1);
  if (h.length === 3) h = h.split('').map((x) => x + x).join('');
  const [r, g, b] = [0, 2, 4]
    .map((i) => parseInt(h.slice(i, i + 2), 16) / 255)
    .map((v) => (v <= 0.03928 ? v / 12.92 : ((v + 0.055) / 1.055) ** 2.4));
  const lum = 0.2126 * r + 0.7152 * g + 0.0722 * b;
  const vsWhite = 1.05 / (lum + 0.05);
  const vsInk = (lum + 0.05) / (0.0137 + 0.05); // #0f172a
  return vsInk > vsWhite ? '#0f172a' : '#ffffff';
}
